// Room template parsing (Phaser-free so tests and tools can use it).
//
// Templates are ASCII grids on the 48 px tile grid (legend in docs/ARCHITECTURE.md):
//   #  solid          =  one-way platform   ^  hazard (solid, damages the player)
//   .  empty          b  decorative back wall (non-colliding)
//   P  player spawn   X  exit gate          g  ground spawn   a  air spawn
//   O  orb spot       S  service station    R  repair bay     C  crate
//   T  challenge terminal                   B  boss anchor (centre-bottom of the boss)
// Marker cells are empty for physics. Wide props (B, X, S, R, P) may be authored as a
// horizontal run of the same letter ("BB"); the run's centre becomes the anchor.

import type { NodeType } from '../../core/types';
import type { RoomTemplate } from '../../data/rooms';

/** Physics kind per cell. */
export const CELL = {
  EMPTY: 0,
  SOLID: 1,
  ONEWAY: 2,
  HAZARD: 3,
} as const;
export type CellKind = (typeof CELL)[keyof typeof CELL];

export type MarkerChar = 'P' | 'X' | 'g' | 'a' | 'O' | 'S' | 'R' | 'C' | 'T' | 'B';
export const MARKER_CHARS: readonly MarkerChar[] = ['P', 'X', 'g', 'a', 'O', 'S', 'R', 'C', 'T', 'B'];
/** Markers whose horizontal runs merge into a single anchor. */
const RUN_MARKERS = new Set<MarkerChar>(['P', 'X', 'S', 'R', 'B']);
export const LEGEND = '#=^.bPXgaOSRCTB';

/** Marker position in tile units: cx is the horizontal centre (may be fractional), ty the row. */
export interface MarkerPos {
  cx: number;
  ty: number;
}

export interface RoomLayout {
  id: string;
  type: NodeType;
  name: string;
  /** Size in tiles. */
  w: number;
  h: number;
  /** Physics kind per cell, index y * w + x. */
  cells: Uint8Array;
  /** 1 where a decorative back wall sits behind the cell. */
  back: Uint8Array;
  markers: Record<MarkerChar, MarkerPos[]>;
}

/** Parse a template; throws on malformed input (ragged rows, unknown glyphs). */
export function parseRoom(t: RoomTemplate): RoomLayout {
  const h = t.tiles.length;
  if (h === 0) throw new Error(`room ${t.id}: empty template`);
  const w = t.tiles[0].length;
  const cells = new Uint8Array(w * h);
  const back = new Uint8Array(w * h);
  const markers = {} as Record<MarkerChar, MarkerPos[]>;
  for (const m of MARKER_CHARS) markers[m] = [];

  for (let y = 0; y < h; y++) {
    const row = t.tiles[y];
    if (row.length !== w) throw new Error(`room ${t.id}: row ${y} has ${row.length} columns, expected ${w}`);
    for (let x = 0; x < w; x++) {
      const ch = row[x];
      if (!LEGEND.includes(ch)) throw new Error(`room ${t.id}: unknown glyph '${ch}' at ${x},${y}`);
      const i = y * w + x;
      if (ch === '#') cells[i] = CELL.SOLID;
      else if (ch === '=') cells[i] = CELL.ONEWAY;
      else if (ch === '^') cells[i] = CELL.HAZARD;
      else if (ch === 'b') back[i] = 1;
    }
  }

  // Markers: runs of wide-prop letters collapse to their centre.
  for (let y = 0; y < h; y++) {
    const row = t.tiles[y];
    let x = 0;
    while (x < w) {
      const ch = row[x] as MarkerChar;
      if (!MARKER_CHARS.includes(ch)) {
        x++;
        continue;
      }
      if (RUN_MARKERS.has(ch)) {
        let end = x;
        while (end + 1 < w && row[end + 1] === ch) end++;
        markers[ch].push({ cx: (x + end + 1) / 2, ty: y });
        x = end + 1;
      } else {
        markers[ch].push({ cx: x + 0.5, ty: y });
        x++;
      }
    }
  }

  // Marker and platform cells sitting inside a back-wall region inherit the wall so
  // they never punch a hole into it (the template can only hold one glyph per cell).
  const isB = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && t.tiles[y][x] === 'b';
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ch = t.tiles[y][x];
      if (ch === 'b' || ch === '.' ) continue;
      const n = (isB(x - 1, y) ? 1 : 0) + (isB(x + 1, y) ? 1 : 0) + (isB(x, y - 1) ? 1 : 0) + (isB(x, y + 1) ? 1 : 0);
      const vertical = isB(x, y - 1) || isB(x, y + 1);
      if (n >= 2 || (ch !== '#' && ch !== '^' && vertical)) back[y * w + x] = 1;
    }
  }

  return { id: t.id, type: t.type, name: t.name, w, h, cells, back, markers };
}

/** Physics kind at a tile; outside the map counts as solid except above the top edge (open sky). */
export function cellAt(l: RoomLayout, x: number, y: number): CellKind {
  if (x < 0 || x >= l.w || y >= l.h) return CELL.SOLID;
  if (y < 0) return CELL.EMPTY;
  return l.cells[y * l.w + x] as CellKind;
}

/** Full collision on every face (solid or hazard). */
export function isBlocking(k: CellKind): boolean {
  return k === CELL.SOLID || k === CELL.HAZARD;
}

/** Something you can stand on (solid, hazard or one-way). */
export function isGround(k: CellKind): boolean {
  return k !== CELL.EMPTY;
}

/** Row of the first ground cell at or below row `fromRow` in column x, or -1. */
export function groundRowBelow(l: RoomLayout, x: number, fromRow: number): number {
  for (let y = Math.max(0, fromRow); y < l.h; y++) {
    if (isGround(cellAt(l, x, y))) return y;
  }
  return -1;
}
